import { getLocalePath } from '@src/components/seo/seo-page-router.helper'
import { NEXT_PUBLIC_SITE_URL } from '@src/config/env'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant/BaseProductVariant'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { DYNAMIC_APP_LINK } from '../../../utils/constants'

function withLbUserId(url: string, lbUserId?: string): string {
  if (!lbUserId?.trim() || !URL.canParse(url)) return url
  const u = new URL(url)
  u.searchParams.set('web_lb_user_id', lbUserId.trim())
  return u.toString()
}

export function buildPageUrl({
  locale,
  path,
  product,
  selectedOptions,
  lbUserId,
}: {
  slug?: string
  /**
   * Optional explicit pathname to deep-link to. When provided, takes precedence
   * over `product` and the current-window fallback — used by Live Event LP CTAs
   * to point at lifecycle-specific destinations (e.g. `/live-video-player`)
   * rather than the page the user is on.
   */
  path?: string
  locale: string
  product?: Product
  selectedOptions?: ProductVariantOption[]
  lbUserId?: string
}) {
  let baseUrl: string
  if (path) {
    const trimmed = path.startsWith('/') ? path.substring(1) : path
    baseUrl = `${NEXT_PUBLIC_SITE_URL}/${getLocalePath(locale, trimmed)}`
  } else if (!product && isBrowser()) {
    baseUrl = window.location.href
  } else {
    const productLink = product ? getProductLink(product, selectedOptions ?? []) : ''
    const productPath = productLink.startsWith('/') ? productLink.substring(1) : productLink
    baseUrl = `${NEXT_PUBLIC_SITE_URL}/${getLocalePath(locale, productPath)}`
  }
  return withLbUserId(baseUrl, lbUserId)
}

// AppsFlyer onelinks are shared across envs and the destination must always
// resolve in production — non-prod hosts (dev/integ/preview) won't be installed
// app deeplinks and cannot serve the marketing redirect on a freshly installed
// device. Force the prod origin on the embedded `deep_link_value` so dev
// builds produce a link that's safe to share / scan.
// @i18n-ignore-start
const DEEP_LINK_PROD_ORIGIN = 'https://www.quince.com'
// @i18n-ignore-end

function withProdOrigin(pageUrl: string): string {
  if (!URL.canParse(pageUrl)) return pageUrl
  const u = new URL(pageUrl)
  const prod = new URL(DEEP_LINK_PROD_ORIGIN)
  // Already pointing at prod — return the input unmodified so callers don't
  // observe URL-normalisation side-effects (unicode → percent-encoding, etc).
  if (u.protocol === prod.protocol && u.hostname === prod.hostname && u.port === prod.port) {
    return pageUrl
  }
  u.protocol = prod.protocol
  u.hostname = prod.hostname
  u.port = prod.port
  return u.toString()
}

/**
 * Builds an AppsFlyer OneLink with the given page URL embedded as
 * `deep_link_value`. The default OneLink base (`DYNAMIC_APP_LINK`) covers the
 * site-wide "shop in app" flow; per-surface overrides (e.g. Live Event LP CTAs
 * authored against a dedicated OneLink template in AppsFlyer) can pass
 * `onelinkBase` to swap the template without affecting any other surface.
 *
 * `onelinkBase` falls back to `DYNAMIC_APP_LINK` when it is falsy or not a
 * parseable URL — so a bad / empty Contentful authoring value degrades to the
 * site default rather than producing a broken link.
 */
export function buildAppDeepLinkUrl(pageUrl: string, onelinkBase?: string | null): string {
  const prodPageUrl = withProdOrigin(pageUrl)
  const encodedUrl = encodeURIComponent(prodPageUrl)
  const base = onelinkBase && URL.canParse(onelinkBase) ? onelinkBase : DYNAMIC_APP_LINK
  const url = new URL(base)
  url.searchParams.set('deep_link_value', encodedUrl)

  return url.toString()
}
